import _ from 'lodash';

import { SmartenitPlugin } from '../smartenit-plugin';
import { IColor } from '../../interfaces/color.interface';
import { IWebSocketDeviceMessage } from '../../websockets/websocket-device-message.interface';
import {
  IColorControlServerMode,
  ColorControlServerHueSaturationMode,
  ColorControlServerXYMode,
  ColorControlServerTemperatureMode
} from './modes/index';
import { ColorUtils } from './color.utils';

export class ColorControlServerPlugin extends SmartenitPlugin
  implements IColor {
  private static readonly MODES: { [key: string]: IColorControlServerMode } = {
    ColorHueSaturation: new ColorControlServerHueSaturationMode(),
    ColorTemperature: new ColorControlServerTemperatureMode(),
    ColorXY: new ColorControlServerXYMode()
  };

  private mode: IColorControlServerMode = ColorControlServerPlugin.MODES
    .ColorHueSaturation;

  public onInit() {
    this.updateMode();
  }

  public getReportableAttrs() {
    return ['CurrentHue', 'CurrentSaturation', 'CurrentX', 'CurrentY', 'ColorTemperatureMireds'];
  }

  public onCustomOptionsChanged() {
    this.updateMode();
  }

  private updateMode() {
    const mode: string = <string> _.get(this.customOptions, 'mode');
    if (
      mode &&
      Object.keys(ColorControlServerPlugin.MODES).indexOf(mode) > -1
    ) {
      this.mode = ColorControlServerPlugin.MODES[mode];
    }
  }

  public getColor(): string {
    return <string> _.get(this.state, 'color');
  }

  public setColor(color: string): void {
    const rgb = ColorUtils.HEXtoRGB(color);
    if (rgb) {
      this.state = Object.assign({}, this.state, { color });
      this._onUpdate.next({ state: this.state });
      this.executeMethod(
        this.componentId,
        this.processorName,
        this.mode.setColorMethod,
        this.mode.buildPayload(rgb),
        true
      );
    }
  }

  public getColorMode() {
    this.getAttributeWithSubscribeOption(
      this.componentId,
      this.processorName,
      'ColorMode',
      null,
      false,
      'cached=false'
    );
  }

  public getEffectMethod(context?: any): string {
    return this.mode.setColorMethod;
  }

  public getEffectColor(params: any): string | null {
    const color = this.mode.getEffectColor(params);
    return color ? ColorUtils.RGBtoHEX(color.r, color.g, color.b) : null;
  }

  public getEffectColorName(params: any): string | null {
    const color = this.mode.getEffectColorName(params);
    return color ? color : null;
  }

  public buildPayload(color: string): any {
    const rgb = ColorUtils.HEXtoRGB(color);
    return rgb ? this.mode.buildPayload(rgb) : null;
  }

  get stateTTL(): number {
    return 2 * 60;
  }

  public getStatusPayload(): any {
    return this.mode.statusPayload;
  }

  public processMessage(message: IWebSocketDeviceMessage, timestamp?: number) {
    const attribute: string = <string> _.get(message, 'attributeOrMethod');
    const data: any = _.get(message, 'data.response');

    if (attribute === 'CurrentHue') {
      this.state = Object.assign({}, this.state, { currentHue: data.value });
    } else if (attribute === 'CurrentSaturation') {
      this.state = Object.assign({}, this.state, {
        currentSaturation: data.value
      });
    } else if (attribute === 'CurrentX') {
      this.state = Object.assign({}, this.state, { currentX: data.value });
    } else if (attribute === 'CurrentY') {
      this.state = Object.assign({}, this.state, { currentY: data.value });
    } else if (attribute === 'ColorTemperatureMireds') {
      this.state = Object.assign({}, this.state, {
        colorTemperatureMireds: data.value
      });
    }

    this.computateColor();
    this._onUpdate.next({ state: this.state });

    return this.state;
  }

  public computateColor() {
    const color = this.mode.computateColor(this.state);
    if (color) {
      this.state = Object.assign({}, this.state, {
        color: ColorUtils.RGBtoHEX(color.r, color.g, color.b)
      });
      this._onUpdate.next({ state: this.state });
    }
  }
}
